import React, { useCallback } from "react";

import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { MenuProps } from "antd/es/menu";
import { useIntl } from "react-intl";

import { Dropdown, ListWithOverflowItem } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";

import GlobalSearchFilter, {
  getGlobalSearchFilterDisplayName,
} from "../../enum/GlobalSearchFilter";

import styles from "./TypeSelector.module.scss";

const typeList = [
  GlobalSearchFilter.ALL,
  GlobalSearchFilter.COMPANIES,
  GlobalSearchFilter.PEOPLE,
  GlobalSearchFilter.DEALS,
  GlobalSearchFilter.GROUPS,
  GlobalSearchFilter.ACTIVITIES,
  GlobalSearchFilter.ROUTES,
  GlobalSearchFilter.TERRITORIES,
  GlobalSearchFilter.RESOURCES,
  GlobalSearchFilter.USERS,
] as const;

interface TypeSelectorProps {
  onSelect: (filter: GlobalSearchFilter) => void;
  value: GlobalSearchFilter;
}

const TypeSelector = ({ onSelect, value }: TypeSelectorProps) => {
  const intl = useIntl();

  const overflowRender = useCallback(
    (hiddenItemsCount: number) => {
      const overflownItems = typeList.slice(-hiddenItemsCount);
      if (!overflownItems.length) {
        return null;
      }

      const menu: MenuProps = {
        items: overflownItems.map((type) => ({
          key: type,
          label: (
            <span className={styles.menuItem}>
              {getGlobalSearchFilterDisplayName(intl, type)}
              {value === type && <FontAwesomeIcon className={styles.checkIcon} icon={faCheck} />}
            </span>
          ),
          onClick: () => onSelect(type),
        })),
        selectable: true,
        selectedKeys: [value],
      };

      return (
        <Dropdown menu={menu}>
          <RoundButton
            active={overflownItems.includes(value)}
            icon={<FontAwesomeIcon icon={faEllipsisH} />}
          />
        </Dropdown>
      );
    },
    [intl, onSelect, value]
  );

  return (
    <div className={styles.container}>
      <ListWithOverflowItem overflowRender={overflowRender}>
        {typeList.map((filter) => (
          <RoundButton
            active={filter === value}
            className={styles.button}
            key={filter}
            onClick={() => onSelect(filter)}
          >
            {getGlobalSearchFilterDisplayName(intl, filter)}
          </RoundButton>
        ))}
      </ListWithOverflowItem>
    </div>
  );
};

export default TypeSelector;
